import { InvitationDto } from '../../services/client-api-wrapper/identity_provider/responses.dto';
import jwtDecode from 'jwt-decode';
import { useEffect, useState } from 'react';
import { Params, useNavigate } from 'react-router';
import { useParams } from 'react-router-dom';
import { useRecoilState } from 'recoil';
import { aLandingPageClickedGoIn } from 'routes/WaitingArea/atom';
import { idpService } from 'services/api/identity-provider.service';
import { resourceService } from 'services/api/resource.service';
import { useIdentityClaims, useLocalProfile } from 'shared-state/identity/hooks';
import { IIdentityClaims } from 'shared-state/identity/types';
import { useCurrentAreaId, useCurrentCampusId, useCurrentRoomId, useIsVisitorAtLandingPage } from 'shared-state/location/hook';
export interface ILandingPageRouteParams extends Params {
    formattedOrgName: string;
    customPath: string;
    invitationId: string;
    slug: string;
}

export interface ILandingPageState {
    videntityClaims?: IIdentityClaims;
    slug?: string;
    customUrl: string;
    invitationId?: string;
    eventName: string;
    orgId: string;
    isLoadingDialogVisible: string;
    tokenPayload: any;
    goInClicked: (name?: string) => void;
}

export const useLandingPageState: () => ILandingPageState = () => {
    const params = useParams<ILandingPageRouteParams>();
    const slug = params.slug;
    const customUrl = `${params.formattedOrgName}/${params.customPath}`;
    const invitationId = params.invitationId;
    const routeHistory = useNavigate();
    const [, setCurrentRoomId] = useCurrentRoomId();
    const [, setCurrentAreaId] = useCurrentAreaId();
    const [, setCurrentCampusId] = useCurrentCampusId();
    const [videntityClaims, setVidentityClaims] = useIdentityClaims();
    const localProfile = useLocalProfile();
    const [orgId, setOrgId] = useState('');
    const [, setVisitorName] = useState('');
    const [eventName, setEventName] = useState('');
    const [tokenPayload, setTokenPayload] = useState<any>({});
    const [isLoadingDialogVisible, setIsLoadingDialogVisible] = useState<string>('true');
    const [selectedInvitation, setSelectedInvitation] = useState<InvitationDto | any>();
    const [, setLandingPageClickedGoIn] = useRecoilState(aLandingPageClickedGoIn);
    const [, setIsVisitorAtLandingPage] = useIsVisitorAtLandingPage();
    const [visitorAccessPassId, setVisitorAccessPassId] = useState('');
    const [accessPass, setAccessPass] = useState<any>(null);

    localStorage.setItem('jitsiSessionId', localProfile?.uid || '');

    const invitation = async () => {
        if (slug) {
            const res = await idpService.showAccessPassBySlug(slug);
            console.log('🚀 ~ invitation ~ res:', res);
            setAccessPass(res);
            setOrgId(res.orgId);
            setEventName(res.access_name);
            setVisitorAccessPassId(res.access_pass_id);
        } else {
            const res = await idpService.showInvitation(customUrl, invitationId);
            setSelectedInvitation(res);
            setOrgId(res.orgId);
            setEventName(res.event_name);
        }
    };

    useEffect(() => {
        const token = localStorage.getItem('ivAccessToken');
        if (!token) return;
        const payload = jwtDecode<any>(token);
        setTokenPayload(payload);
        setCurrentAreaId(payload.areaId);
        setCurrentCampusId(payload.campusId);
        setVisitorName(payload.name);
        const identity: IIdentityClaims = {
            uid: payload.sub.split(':')[2],
            displayName: payload.name,
            roles: [payload.role],
            type: 'visitor',
            tid: payload.orgId,
            upn: payload.email,
            areas: ['*'],
            idp: 'ivicos'
        };
        setVidentityClaims(identity);
        setCurrentAreaId('waiting-area');
        invitation();
        setIsVisitorAtLandingPage(window.location.pathname.includes('/visitorLandingPage'));
    }, []);

    useEffect(() => {
        videntityClaims && setCurrentRoomId('visitor-' + videntityClaims.uid);
    }, [videntityClaims]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsLoadingDialogVisible('false');
        }, 1000);
        return () => clearTimeout(timer);
    }, []);

    sessionStorage.setItem('showLoadingAnimation', isLoadingDialogVisible);

    const getAccessToken = () => {
        const accessToken = localStorage.getItem('ivAccessToken');
        if (!accessToken) {
            throw new Error('No Token');
        }
        return accessToken;
    };

    const authenticateAutoConfirmedVisitorOrDirectAccessPass = async (id, visitorName, isSlugPresent) => {
        try {
            if (isSlugPresent) {
                await idpService.autoConfirmAccessPass(visitorName, id);
            } else {
                await idpService.autoConfirmVisitor(visitorName, id);
            }

            const accessToken = localStorage.getItem('ivAccessToken');
            if (!accessToken) {
                throw new Error('No Token');
            }

            const { sub, role, campusId, areaId, roomId } = jwtDecode<any>(getAccessToken());

            const destinationRoom = await resourceService.getRoomById(campusId, areaId, roomId);
            if (role === 'auto-confirmed-visitor') {
                if (destinationRoom.isAudioOnly) {
                    sessionStorage.setItem(`noVideo-${destinationRoom.id}`, destinationRoom.id);
                }
            }

            if (areaId && roomId) {
                if (Array.isArray(destinationRoom.whitelist) && destinationRoom.isPrivate) {
                    sessionStorage.setItem('whitelist-roomId', destinationRoom.id);
                }
                sessionStorage.setItem(
                    'visitorIdentity',
                    JSON.stringify({
                        type: 'visitor-access-token',
                        uid: sub.split(':')[2],
                        role: role,
                        campusId: campusId,
                        areaId: areaId
                    })
                );
                routeHistory(`/areas/${areaId}/rooms/${roomId}`);
                setIsVisitorAtLandingPage(false);
            }
        } catch (err) {
            console.error('Error Found:', err);
        }
    };

    const authenticateUnConfirmedVisitor = async (id, visitorName, isSlugPresent) => {
        try {
            if (isSlugPresent) {
                await idpService.authAccessPass(visitorName, id);
            } else {
                await idpService.authVisitor(visitorName, id);
            }
            const tokenPayload = jwtDecode<any>(getAccessToken());

            if (tokenPayload) {
                const visitorId = customUrl !== 'undefined/undefined' ? customUrl : id;
                const redirectVisitorToLandingPage = isSlugPresent ? `/w-access-pass/${slug}` : `/w/${visitorId}`;
                setLandingPageClickedGoIn(true);
                routeHistory(redirectVisitorToLandingPage);
                sessionStorage.setItem('isYoutubeVideoVisible', 'true');
            }
        } catch (err) {
            console.error('Error Found:', err);
        }
    };

    const getRandomVisitorNumber = (min: number, max: number): number => {
        return Math.floor(Math.random() * (max - min + 1)) + min;
    };

    const goInClicked = (name) => {
        if (!selectedInvitation && !accessPass) return;

        let userName = name;
        if (!name) {
            const randomNumber = getRandomVisitorNumber(1, 999);
            userName = `Campus Visitor${randomNumber}`;
        }

        if (accessPass) {
            const { area, room, access_pass_id, access_method } = accessPass;
            if (area && room && access_method === 'direct') {
                authenticateAutoConfirmedVisitorOrDirectAccessPass(access_pass_id, userName, true);
            } else {
                authenticateUnConfirmedVisitor(access_pass_id, userName, true);
            }
        } else {
            const { default_destination, invitationId } = selectedInvitation;
            if (default_destination.areaId && default_destination.roomId) {
                authenticateAutoConfirmedVisitorOrDirectAccessPass(invitationId, userName, false);
            } else {
                authenticateUnConfirmedVisitor(invitationId, userName, false);
            }
        }
    };

    return {
        videntityClaims,
        slug,
        customUrl,
        invitationId,
        eventName,
        orgId,
        tokenPayload,
        isLoadingDialogVisible,
        goInClicked
    };
};
